import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Hammer, TrendingUp, Calculator, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { analytics } from '@/utils/analytics';
import { cn } from '@/utils/cn';

interface WorkScenariosCallToActionProps {
  dpeId: string;
  className?: string;
}

export const WorkScenariosCallToAction: React.FC<WorkScenariosCallToActionProps> = ({
  dpeId,
  className
}) => {
  const navigate = useNavigate();

  // La page scénarios travaux gère les données manquantes via enrichDPEWithEstimates().
  // On affiche toujours le bouton — plus de détection legacy ici.
  
  const handleNavigate = () => {
    analytics.trackEvent({
      category: 'conversion',
      action: 'click_work_scenarios_cta',
      label: dpeId,
      custom_parameters: {
        source: 'results_page'
      }
    });

    navigate(`/analysis/${dpeId}/scenarios`);
  };

  return (
    <Card className={cn(
      'border-2 border-blue-500 bg-gradient-to-br from-blue-50 via-white to-green-50',
      className
    )}>
      <CardContent className="p-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
            <Hammer className="w-8 h-8 text-blue-600" />
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Combien pouvez-vous économiser sur vos factures ?
          </h3>

          <p className="text-lg text-gray-600 mb-8 max-w-2xl">
            Simulez différents scénarios de travaux et découvrez combien vous pouvez gagner en classe DPE — et en euros par an
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 w-full max-w-3xl">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                <Calculator className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Scénarios sur-mesure</h4>
              <p className="text-sm text-gray-600">
                Comparez isolation, chauffage, fenêtres…
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Gain de classe DPE</h4>
              <p className="text-sm text-gray-600">
                Visualisez le saut de lettre atteignable
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Économies en €/an</h4>
              <p className="text-sm text-gray-600">
                Estimez vos gains financiers et le ROI
              </p>
            </div>
          </div>

          <Button
            variant="primary"
            size="lg"
            onClick={handleNavigate}
            icon={<Hammer className="w-5 h-5 mr-2" />}
            className="shadow-lg hover:shadow-xl transition-shadow"
          >
            Calculer mes économies potentielles
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
